import './SliderUiObject.scss';
import React, { useState } from 'react';
import { Box, Grid, Slider, ThemeProvider, useMediaQuery, useTheme } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { max, min } from 'lodash';

import NumberSelector from './NumberSelector';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { cacheLtr, useLanguage } from '../../utils/GeneralUtils';
import { safeStartSessionRecording } from '../../utils/safePosthog';
import { usePriceExplanation } from '../hooks/usePriceExplanation';
import { InlinePriceLine } from '../components/InlinePriceLine';
import { customerFacingPriceFormatter } from '../utils';
import { withBookingFee } from '../core/pricing';

const SliderUiObject = (props) => {
  const posthog = usePostHog();
  const { uiObject, orderState, setOrderDetailValue, type, isLoadingDates } = props;

  const language = useLanguage();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const shouldLimitSliderMax = !!orderState.specificEvent;

  const [sliderValue, setSliderValue] = useState(max([orderState.details[uiObject.data_type], uiObject.min_value]));
  const { shouldExplain, rateAtMin, rateAtMax } = usePriceExplanation({ personsOverride: sliderValue });
  // Several sliders can share a page; only the one the pricing engine reads should talk about money.
  const drivesPrice =
    type !== 'edit' && orderState.orderPage.objects?.find((o) => o.type === 'slider')?.id === uiObject.id;
  // A specific event caps the max at its remaining tickets, so the top mark would quote a price for
  // a party size the customer cannot actually pick. Drop the mark prices rather than lie on them.
  const showPrice = drivesPrice && shouldExplain && !shouldLimitSliderMax;

  let minValue = type === 'edit' ? 1 : uiObject.min_value;
  // to allow admins create bookings for even one participant
  let maxValue = shouldLimitSliderMax
    ? min([orderState.specificEvent?.tickets_left, uiObject.max_value])
    : uiObject.max_value;

  if (minValue === maxValue && type !== 'edit') {
    return null;
  }

  const unit = orderState.orderPage?.price_unit_label?.[language];
  const markLabel = (value, rate) => (
    <Box>
      <OrderPageTypography>{value}</OrderPageTypography>
      {showPrice && rate != null && (
        <>
          <OrderPageTypography className="mark-price" style={{ whiteSpace: 'nowrap' }} dir={i18n.dir()}>
            {customerFacingPriceFormatter(withBookingFee(rate, orderState.account), orderState.account)}
          </OrderPageTypography>
          <OrderPageTypography className="mark-price" style={{ whiteSpace: 'nowrap' }}>
            {unit ? t('order_page.perUnitShort', { unit }) : t('order_page.perPersonShort')}
          </OrderPageTypography>
        </>
      )}
    </Box>
  );

  const onSliderCommit = (val) => {
    safeStartSessionRecording(posthog);
    setOrderDetailValue(uiObject.data_type, val);
  };
  let marks = [
    {
      value: minValue,
      label: markLabel(minValue, rateAtMin),
    },
    {
      value: maxValue,
      label: markLabel(maxValue, rateAtMax),
    },
  ];

  if (type === 'edit' && uiObject.dashboardNoLimit) {
    marks = [
      {
        value: 1,
        label: <OrderPageTypography>1</OrderPageTypography>,
      },
      {
        value: 100,
        label: <OrderPageTypography>100</OrderPageTypography>,
      },
    ];
    minValue = 1;
    maxValue = 100;
  }
  const sliderText = uiObject.label[language];
  return (
    <Grid container flexDirection="column">
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <OrderPageTypography variant="h3" bold>
            {sliderText}
          </OrderPageTypography>
        </Grid>
        <Grid>
          <NumberSelector
            value={sliderValue}
            setValue={(val) => {
              setSliderValue(val);
              onSliderCommit(val);
            }}
            disabled={minValue === maxValue || isLoadingDates}
            min={minValue}
            max={maxValue}
          />
        </Grid>
      </Grid>
      <Grid item my={isSmallScreen ? 0 : 3} mx={3} pb={showPrice ? 4.5 : 0}>
        <CacheProvider value={cacheLtr}>
          <ThemeProvider theme={{ ...theme, direction: 'ltr' }}>
            <Slider
              defaultValue={minValue}
              value={sliderValue}
              min={minValue}
              max={maxValue}
              marks={marks}
              disabled={minValue === maxValue}
              onChange={(e, val) => setSliderValue(val)}
              onChangeCommitted={(e, val) => onSliderCommit(val)}
              aria-label="Default"
              sx={{
                '& .MuiSlider-markLabel': {
                  fontSize: '16px !important',
                },
                '& .MuiSlider-markLabel *': {
                  fontSize: '16px !important',
                },

                '& .MuiSlider-markLabel .mark-price': {
                  fontSize: '13px !important',
                  lineHeight: 1.15,
                  opacity: 0.6,
                },
              }}
            />
          </ThemeProvider>
        </CacheProvider>
      </Grid>

      {drivesPrice && <InlinePriceLine personsOverride={sliderValue} />}

      {uiObject.post_label && (
        <div className="text-label" dangerouslySetInnerHTML={{ __html: uiObject.post_label[language] }} />
      )}
    </Grid>
  );
};

export default SliderUiObject;
